import { Clock, Lock } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Box, HStack, Text } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { WindowStatus } from '../types';

interface WindowStatusBannerProps {
  window: WindowStatus | undefined;
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return '0:00';
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  if (hours > 0) return `${hours}h ${minutes}m`;
  const seconds = Math.floor((ms % 60_000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function WindowStatusBanner({ window: windowStatus }: WindowStatusBannerProps) {
  const [remaining, setRemaining] = useState(windowStatus?.remainingMs ?? 0);

  useEffect(() => {
    if (!windowStatus) return;
    setRemaining(windowStatus.remainingMs);

    if (!windowStatus.isOpen) return;

    const interval = setInterval(() => {
      setRemaining((prev) => {
        const next = prev - 1000;
        if (next <= 0) {
          clearInterval(interval);
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [windowStatus]);

  if (!windowStatus) return null;

  const isOpen = windowStatus.isOpen && remaining > 0;

  return (
    <Box style={[styles.container, isOpen ? styles.open : styles.closed]}>
      <HStack spacing="sm" align="center" justify="center">
        {isOpen ? (
          <Clock size={14} color={colors.status.success.main} />
        ) : (
          <Lock size={14} color={colors.accent.amber.neon} />
        )}
        <Text
          variant="caption"
          color={isOpen ? colors.status.success.light : colors.accent.amber.neon}
          style={styles.text}
        >
          {isOpen
            ? `Window open — ${formatCountdown(remaining)} remaining`
            : 'Window closed — template required'}
        </Text>
      </HStack>
    </Box>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.md,
    borderBottomWidth: 1,
  },
  open: {
    backgroundColor: colors.status.success.bg,
    borderBottomColor: colors.status.success.dark,
  },
  closed: {
    backgroundColor: colors.status.warning.bg,
    borderBottomColor: colors.status.warning.dark,
  },
  text: {
    fontWeight: tokens.fontWeight.medium,
    fontSize: 11,
  },
});
